// Prices (TD-083, DD-048): the website's "from" prices. The listini on the left, the active one being the latest
// started, the selected one's lines on the right, each marked against the listino before it. Every listino stays
// editable and deletable, started or not, because nothing on a contract reads it. Clicking a line opens it (DD-045), a
// removed one too, where Add back is its step. A new listino, blank or from a card, opens a modal for its name and
// start first (DD-006). The service lines and work types open from the header, a service line with its split
// defaults (TD-078), renamed or deleted while unused (TD-099). Each line shows its description and what its price includes
// under it (the user 2026-10-30), and the
// listino before is a column only when there is one (finance-real round 3). The selected listino reads as one line above
// its table, with no strip and no filter bar (finance-real round 2).
import type { ReactNode } from "react";
import { VersionsLayout, type VersionEntry } from "@/components/PageLayout/VersionsLayout";
import { SpinnerIcon } from "@/components/icons";
import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Callout } from "@/components/ui/Callout";
import { DataTable, type DataColumn } from "@/components/ui/DataTable";
import { MoneyText } from "@/components/ui/MoneyText";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { cn } from "@/utils/cn";
import { formatMoney } from "@/utils/currency";
import { useFinanceParams } from "../hooks/useFinanceParams";
import { useListini, useServiceLines } from "../hooks/usePricing";
import type { Catalog } from "../services/pricingService";
import type { Listino, ListinoInclude, ListinoStatus } from "../types";
import { dateLabel, labelOf } from "../utils/format";
import { isChanged, listinoRows, type ListinoRow } from "../utils/listinoDiff";
import { CatalogListModal, CatalogRowModal } from "./CatalogModals";
import { AddLineModal, EditListinoModal, LineModal, NewListinoModal, RemovedLineModal } from "./ListinoModals";

const STATUS: Record<ListinoStatus, [string, BadgeTone]> = { active: ["Active", "green"], scheduled: ["Scheduled", "blue"], past: ["Past", "gray"] };
const LISTINO = ENTITY_CONFIG.listino;

/** The listino named in the URL, else the active one, else the newest. */
export const selectedListino = (listini: Listino[], id: string | null): Listino | undefined =>
  listini.find((l) => l.id === id) ?? listini.find((l) => l.status === "active") ?? listini[0];

function when(l: Listino): string {
  if (l.status === "scheduled") return `starts ${dateLabel(l.startDate)}`;
  if (l.status === "active") return `since ${dateLabel(l.startDate)}`;
  return `${dateLabel(l.startDate)} to ${dateLabel(l.until)}`;
}

function ChangeBadge({ row, hasPrevious, currency }: { row: ListinoRow; hasPrevious: boolean; currency: string }) {
  if (!hasPrevious) return null;
  if (row.removed) return <Badge tone="red">Removed</Badge>;
  const prev = row.prev;
  if (!prev) return <Badge tone="green">New</Badge>;
  const delta = row.line.fromPriceMinor - prev.fromPriceMinor;
  if (!delta) return <span className="text-xs text-gray-400">Same</span>;
  return <Badge tone="amber">{delta > 0 ? "+" : "−"}{formatMoney(Math.abs(delta), currency)}</Badge>;
}

/** A line's items on one row under it: "Shooting 150,00 € · Management 250,00 € · Ads 18%". */
export const includesText = (includes: ListinoInclude[], currency: string): string =>
  includes.map((i) => `${i.label} ${i.pct !== null ? `${i.pct}%` : formatMoney(i.amountMinor ?? 0, currency)}`).join(" · ");

export function PricesTab() {
  const { params, set, closeModals } = useFinanceParams();
  const listiniQuery = useListini();
  const serviceLinesQuery = useServiceLines();
  const serviceLines = serviceLinesQuery.data ?? [];
  // The catalogs, opened from the header (TD-099): their list, then a row's modal in its place (DD-045). They
  // need no listino, so they open in every state of the tab, loading and failed included. The list and a row
  // replace each other in the history, both ways, so browser Back never lands on the list twice. X or Escape on
  // either closes both.
  const catalogParam = params.get("modal");
  const catalog: Catalog | null = catalogParam === "work-types" || catalogParam === "service-lines" ? catalogParam : null;
  const itemId = params.get("item");
  const closeCatalog = () => closeModals({ modal: null, item: null });
  const catalogs = !catalog ? null : itemId ? (
    <CatalogRowModal key={`${catalog}-${itemId}`} catalog={catalog} id={itemId} onBack={() => set({ item: null }, true)} onClose={closeCatalog} />
  ) : (
    <CatalogListModal catalog={catalog} onOpen={(id) => set({ item: id }, true)} onClose={closeCatalog} />
  );

  if (listiniQuery.isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <SpinnerIcon className="w-6 h-6 text-gray-400" />
        {catalogs}
      </div>
    );
  }
  // Never show the empty "create the first listino" state for a fetch that failed.
  if (!listiniQuery.data) {
    return (
      <div className="flex-1 flex items-center justify-center px-4">
        <Callout tone="red" className="flex items-center gap-3">
          <span>Could not load the listini.</span>
          <Button size="sm" variant="secondary" onClick={() => void listiniQuery.refetch()} isLoading={listiniQuery.isFetching}>Retry</Button>
        </Callout>
        {catalogs}
      </div>
    );
  }
  const listini = listiniQuery.data.listini;
  const today = listiniQuery.data.today;
  const listino = selectedListino(listini, params.get("listino"));
  const modal = params.get("modal");
  // A new listino opens its modal first (DD-006): a copy of the active one from the header, or of a card's listino.
  // Created, it is selected.
  const newFrom = (source: Listino | null) => set({ modal: "listino-new", copy: source ? source.id : null, confirm: null });
  const newListino = modal === "listino-new" && (
    <NewListinoModal
      source={listini.find((l) => l.id === params.get("copy"))}
      active={listini.find((l) => l.status === "active")}
      listini={listini}
      today={today}
      onCreated={(created) => set({ listino: created.id, line: null, modal: null, copy: null, confirm: null })}
      onClose={() => closeModals({ modal: null, copy: null, line: null })}
    />
  );
  const versions: VersionEntry[] = listini.map((l) => ({
    id: l.id,
    title: l.name,
    meta: when(l),
    status: { label: STATUS[l.status][0], tone: STATUS[l.status][1] },
    // Every listino edits and deletes, started or not: nothing on a contract reads it (TD-083).
    actions: [
      { id: "edit", label: "Edit listino", onClick: () => set({ listino: l.id, modal: "listino-edit", line: null, confirm: null }) },
      { id: "copy", label: `${LISTINO.createLabel} from this`, onClick: () => newFrom(l) },
      { id: "delete", label: "Delete listino", variant: "danger" as const, onClick: () => set({ listino: l.id, modal: "listino-edit", line: null, confirm: "delete" }) },
    ],
  }));
  const layout = (children: ReactNode) => (
    <VersionsLayout
      versions={versions}
      selectedId={listino?.id ?? null}
      onSelect={(id) => set({ listino: id, line: null, confirm: null, modal: null, copy: null })}
      createLabel={LISTINO.createLabel}
      onCreate={() => newFrom(null)}
    >
      {children}
    </VersionsLayout>
  );
  if (!listino) {
    return layout(
      <>
        <div className="flex-1 flex items-center justify-center text-sm text-gray-500">No listino yet. Create the first one: it starts on the day you pick, a past day included.</div>
        {catalogs}
        {newListino}
      </>,
    );
  }

  const previous = listini.find((l) => l.id === listino.previousId);
  const rows = listinoRows(listino, previous);
  const was = (show: boolean, text: string) => (show ? <div className="text-xs text-gray-400">was {text}</div> : null);
  const columns: DataColumn<ListinoRow>[] = [
    {
      key: "l", label: ENTITY_CONFIG.serviceLine.label, width: "w-64",
      render: (r) => (
        <span className="flex flex-col">
          <span className={cn("font-medium", r.removed && "line-through text-gray-400")}>
            {labelOf(serviceLines, r.line.serviceLineId)} <span className="font-normal text-gray-400">{r.line.kind === "monthly" ? "per month" : "one-off"}</span>
          </span>
          {!r.removed && r.line.description && <span className="text-xs text-gray-600 line-clamp-2 whitespace-pre-line">{r.line.description}</span>}
          {!r.removed && r.line.includes.length > 0 && <span className="text-xs text-gray-500 truncate">{includesText(r.line.includes, listino.currency)}</span>}
        </span>
      ),
    },
    {
      key: "f", label: "From", align: "right", width: "w-36",
      render: (r) => (
        <div className={cn(r.removed && "opacity-50")}>
          <MoneyText amountMinor={r.line.fromPriceMinor} currency={listino.currency} strong />
          {was(!!r.prev && r.prev.fromPriceMinor !== r.line.fromPriceMinor, formatMoney(r.prev?.fromPriceMinor ?? 0, listino.currency))}
        </div>
      ),
    },
    // The first listino has nothing to compare with, so no column for it (finance-real round 3).
    ...(previous ? [{ key: "c", label: `Vs ${previous.name}`, width: "w-56", render: (r: ListinoRow) => <ChangeBadge row={r} hasPrevious currency={listino.currency} /> }] : []),
  ];
  const open = listino.lines.find((l) => l.id === params.get("line"));
  // A removed line's row opens it by the line the listino before had (DD-045: the row's click opens its modal).
  const removed = open ? undefined : rows.find((r) => r.removed && r.line.id === params.get("line"));
  // Modals never stack (DD-045): a hand-typed URL naming both shows only the modal.
  const line = modal ? undefined : open;
  const removedLine = modal ? undefined : removed?.line;
  const close = () => closeModals({ modal: null });

  return layout(
    <>
      {/* One line, not a strip: two figures spread over the width read as empty space (finance-real round 2). */}
      <div className="shrink-0 bg-white rounded-lg px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="font-semibold text-gray-900">{listino.name}</span>
        <Badge tone={STATUS[listino.status][1]}>{STATUS[listino.status][0]}</Badge>
        <span className="text-gray-500">{when(listino)}</span>
        <span className="text-gray-500">
          {previous ? `· ${rows.filter(isChanged).length} of ${rows.length} lines changed vs ${previous.name}` : "· the first listino"}
        </span>
      </div>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => `${r.removed ? "x-" : ""}${r.line.id}`}
        onRowClick={(r) => set({ line: r.line.id, modal: null })}
        // A removed row's click opens it, where Add back is its step: its only action, so no menu (DD-045).
        rowMenu={(r) => (r.removed ? [] : [{ id: "remove", label: "Remove line", variant: "danger" as const, onClick: () => set({ line: r.line.id, confirm: "remove", modal: null }) }])}
      />
      {catalogs}
      {line && (
        <LineModal
          key={`${listino.id}-${line.id}`}
          listino={listino}
          line={line}
          serviceLineLabel={labelOf(serviceLines, line.serviceLineId)}
          serviceLines={serviceLines}
          confirmRemove={params.get("confirm") === "remove"}
          onConfirmRemove={(on) => set({ confirm: on ? "remove" : null })}
          onClose={() => closeModals({ line: null, confirm: null })}
        />
      )}
      {removedLine && previous && (
        <RemovedLineModal
          key={`${listino.id}-x-${removedLine.id}`}
          listino={listino}
          line={removedLine}
          previousName={previous.name}
          serviceLineLabel={labelOf(serviceLines, removedLine.serviceLineId)}
          onClose={() => closeModals({ line: null })}
        />
      )}
      {newListino}
      {modal === "add-line" && <AddLineModal listino={listino} serviceLines={serviceLines} loading={serviceLinesQuery.isLoading} onClose={close} />}
      {modal === "listino-edit" && (
        <EditListinoModal
          key={listino.id}
          listino={listino}
          listini={listini}
          today={today}
          confirmDelete={params.get("confirm") === "delete"}
          onConfirmDelete={(on) => set({ confirm: on ? "delete" : null })}
          onDeleted={() => closeModals({ modal: null, listino: null, confirm: null })}
          onClose={() => closeModals({ modal: null, confirm: null })}
        />
      )}
    </>,
  );
}
